import React, { useEffect, useRef } from 'react'
import { useTheme } from '@mui/material/styles'
import Typography from '@/v2/components/common/Typography'
import Label from '@/v2/components/common/Label'
import SVGInline from '@/v2/components/common/SVGInline'
import checkmark from '#/assets/images/icons/v2/checkmark.svg?raw'
import OffBackground from '#/assets/images/v2/off-hours.png'
import { convertMinutesToHours } from '@/v2/utils/convert'
import useTouchDragReschedule from './useTouchDragReschedule'
import { minutesToY } from './timelineUtils'
import {
  Card,
  CardContent,
  CardRow,
  InlineLabels,
  TimeBubble,
  DesktopDescription,
  DesktopLabels,
} from './styles'

// Derive the same content the desktop Event renders (title/subtitle/labels),
// for both appointments and time-off blocks.
const deriveContent = (appt, palette) => {
  if (appt.type === 'appointment') {
    const labels = [...(appt.labels || [])]
    if (appt.reschedule)
      labels.push({ id: 'reschedule', name: 'Rescheduled', color: 'link' })
    if (appt.noShow)
      labels.push({ id: 'no-show', name: 'No Show', color: 'black' })
    return {
      title: appt.appointmentType?.title,
      subtitle: appt.patient?.fullName || '',
      // The room view no longer splits by doctor, so surface the doctor here.
      doctorName: appt.doctor?.fullName ? `Dr. ${appt.doctor.fullName}` : '',
      background: palette[appt.appointmentType.color].type,
      labels,
      isConfirmed: appt.isConfirmed,
      accent: palette[appt.appointmentType.color].label,
    }
  }
  const timeOffTitle =
    appt.room !== null ? 'Room' : appt.doctor !== null ? 'Doctor' : 'Clinic'
  return {
    title: `${timeOffTitle} Blocked Time ${appt.virtual ? '(Virtual)' : ''}`,
    subtitle: (timeOffTitle === 'Doctor' && appt.doctor?.fullName) || '',
    doctorName: '',
    background: `url(${OffBackground})`,
    labels: [],
    isConfirmed: false,
    accent: null,
  }
}

const TimelineCard = ({
  calEvent,
  isMobile = false,
  showDoctorName = true,
  draggable,
  scrollRef,
  laneRef,
  columnIndex,
  columnCount,
  droppableColumns,
  columnTitles,
  startMin,
  endMin,
  pxPerMin,
  top,
  height,
  leftPct,
  widthPct,
  baseStartMin,
  durationMin,
  onOpen,
  onMove,
  onDragActiveChange,
}) => {
  const { palette } = useTheme()
  const nodeRef = useRef(null)

  const appt = calEvent.event

  const {
    dragging,
    previewStartMin,
    previewColumnIndex,
  } = useTouchDragReschedule({
    enabled: draggable,
    nodeRef,
    scrollRef,
    laneRef,
    columnIndex,
    columnCount,
    droppable: droppableColumns,
    baseStartMin,
    durationMin,
    startMin,
    endMin,
    pxPerMin,
    onTap: onOpen,
    onDrop: onMove,
  })

  const {
    title,
    subtitle,
    doctorName,
    background,
    labels,
    isConfirmed,
    accent,
  } = deriveContent(appt, palette)

  // Let the timeline know a drag is in progress, and which chair it's currently
  // over, so it can dim everything else and outline the drop target.
  useEffect(() => {
    onDragActiveChange?.(dragging, dragging ? previewColumnIndex : null)
  }, [dragging, previewColumnIndex, onDragActiveChange])

  // Safety: clear the timeline's drag state if this card unmounts mid-drag.
  useEffect(() => () => onDragActiveChange?.(false, null), [onDragActiveChange])

  const liveTop = dragging
    ? minutesToY(previewStartMin, startMin, pxPerMin)
    : top

  // Sliding the card into another chair. `left`/`width` are percentages of this
  // card's own column, and every column is the same width, so one column over is
  // exactly 100% — the card simply overhangs its column (nothing clips it) and
  // its drag z-index keeps it above the neighbouring cards. While it's over a
  // different chair it takes that column's full width rather than the lane slice
  // it was packed into here, since the packing doesn't apply over there.
  const columnShift =
    dragging && previewColumnIndex != null
      ? previewColumnIndex - columnIndex
      : 0
  const liveLeft = columnShift === 0 ? leftPct : columnShift * 100
  const liveWidth = columnShift === 0 ? widthPct : 100

  const targetColumnTitle =
    columnShift === 0 ? null : columnTitles?.[previewColumnIndex]
  const previewTime = dragging ? convertMinutesToHours(previewStartMin) : ''

  // Only render the rows that actually fit the card's height so short
  // appointments show clean, whole lines instead of clipped half-lines. Rows are
  // a 13px line box (see CardRow) on top of the card's 4px vertical padding.
  const lineCapacity = Math.floor((height - 4) / 13)

  // The mobile card stacks the type over the patient (and the doctor as it
  // grows). The compact line box keeps both on a 15-minute card. Status dots
  // ride inline at the end of the title row (see InlineLabels) so they stay
  // visible on short appointments without needing their own line.
  const showTitle = lineCapacity >= 1
  const showPatient = !!subtitle && lineCapacity >= 2
  const showDoctor = showDoctorName && doctorName && lineCapacity >= 3
  const showLabels = labels.length > 0

  return (
    <Card
      ref={nodeRef}
      data-card
      $top={liveTop}
      $height={height}
      $left={liveLeft}
      $width={liveWidth}
      $background={background}
      $dragging={dragging}
      $draggable={draggable}
      $isMobile={isMobile}
      $isAppointment={appt.type === 'appointment'}
    >
      {isMobile ? (
        <CardContent>
          {showTitle && (
            <CardRow>
              {isConfirmed && (
                <SVGInline
                  svg={checkmark}
                  style={{
                    fill: accent,
                    width: '12px',
                    height: '12px',
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography
                variant="h5"
                fontWeight="500"
                fontSize="12px"
                title={title}
              >
                {title}
              </Typography>
              {showLabels && (
                <InlineLabels>
                  {labels.map(({ id, color, name }) => (
                    <Label key={id} title={name} color={palette[color].label} />
                  ))}
                </InlineLabels>
              )}
            </CardRow>
          )}
          {showPatient && (
            <CardRow>
              <Typography
                variant="h5"
                fontWeight="400"
                fontSize="12px"
                title={subtitle}
              >
                {subtitle}
              </Typography>
            </CardRow>
          )}
          {showDoctor && (
            <CardRow>
              <Typography
                variant="h5"
                fontWeight="400"
                fontSize="11px"
                title={doctorName}
              >
                {doctorName}
              </Typography>
            </CardRow>
          )}
        </CardContent>
      ) : (
        // Desktop reproduces the old Event block exactly: the check, type and
        // patient sit inline on one row and the labels are pushed to the right.
        <>
          <DesktopDescription>
            {isConfirmed && (
              <SVGInline
                svg={checkmark}
                style={{
                  fill: accent,
                  width: '16px',
                  height: '16px',
                  marginLeft: '-6px',
                }}
              />
            )}
            <Typography variant="h5" fontWeight="500" title={title}>
              {title}
            </Typography>
            <Typography variant="h5" fontWeight="400" title={subtitle}>
              {subtitle}
            </Typography>
          </DesktopDescription>
          <DesktopLabels>
            {labels.map(({ id, color, name }) => (
              <Label key={id} title={name} color={palette[color].label} />
            ))}
          </DesktopLabels>
        </>
      )}
      {dragging && (
        <TimeBubble>
          {/* Name the chair too once the card leaves its own column, so a
              sideways drag is confirmable without reading the header row. */}
          {targetColumnTitle
            ? `${targetColumnTitle} · ${previewTime}`
            : previewTime}
        </TimeBubble>
      )}
    </Card>
  )
}

export default TimelineCard
